import { AIQueryApi } from './api/query-api.js'
import { PaymentsError } from './common/payments.error.js'
import { PaymentOptions } from './common/types.js'
import { BasePaymentsAPI } from './api/base-payments.js'
import { PlansAPI } from './api/plans-api.js'
import { ContractsAPI } from './api/contracts-api.js'
import { AgentsAPI } from './api/agents-api.js'
import { AgentRequestsAPI } from './api/requests-api.js'
import { ObservabilityAPI } from './api/observability-api/observability-api.js'
import { ClientRegistry } from './a2a/clientRegistry.js'
import type { PaymentsA2AServerOptions, PaymentsA2AServerResult } from './a2a/server.js'
import { PaymentsA2AServer } from './a2a/server.js'
import { buildPaymentAgentCard } from './a2a/agent-card.js'
import * as mcpModule from './mcp/index.js'
import { OrganizationsAPI } from './api/organizations-api/organizations-api.js'
import { FacilitatorAPI } from './x402/facilitator-api.js'
import { X402TokenAPI } from './x402/token.js'
import { DelegationAPI } from './x402/delegation-api.js'

/**
 * Main class that interacts with the Nevermined payments API.
 * Use `Payments.getInstance` for server-side usage or `Payments.getBrowserInstance` for browser usage.
 * @remarks This API requires a Nevermined API Key, which can be obtained by logging in to the Nevermined App.
 *
 * The library provides methods to manage AI Agents, Plans & process AI Agent Requests.
 *
 * Each of these functionalities is encapsulated in its own API class:
 * - `plans`: Manages AI Plans, including registration and ordering and retrieving plan details.
 * - `agents`: Handles AI Agents, including registration of AI Agents and access token generation.
 * - `requests`: Manages requests received by AI Agents, including validation and tracking.
 * - `observability`: Provides observability and logging utilities for AI Agents with Helicone integration
 */
export class Payments extends BasePaymentsAPI {
  public query!: AIQueryApi
  public plans!: PlansAPI
  public agents!: AgentsAPI
  public requests!: AgentRequestsAPI
  public observability!: ObservabilityAPI
  public organizations!: OrganizationsAPI
  public contracts!: ContractsAPI
  public facilitator!: FacilitatorAPI
  public x402!: X402TokenAPI
  private _a2aRegistry?: ClientRegistry
  private _delegation?: DelegationAPI

  /**
   * Cached MCP integration to preserve configuration (e.g., agentId, serverName)
   * across multiple getter accesses. This ensures callers do not need to retain
   * a reference to a previously configured instance.
   */
  private _mcpIntegration?: ReturnType<typeof mcpModule.buildMcpIntegration>

  /**
   * Exposes A2A server and client registry methods.
   * The client registry is initialized only if getClient is called.
   */
  public get a2a() {
    return {
      /**
       * Starts the A2A server with payment integration.
       * @param options - Server options.
       */
      start: (
        options: Omit<PaymentsA2AServerOptions, 'paymentsService'>,
      ): PaymentsA2AServerResult => PaymentsA2AServer.start({ ...options, paymentsService: this }),

      /**
       * Gets (or creates) a RegisteredPaymentsClient for the given alias.
       * The registry is initialized only on first use.
       * @param options - ClientRegistryOptions.
       */
      getClient: async (options: any) => {
        if (!this._a2aRegistry) {
          this._a2aRegistry = new ClientRegistry(this)
        }
        return await this._a2aRegistry.getClient(options)
      },
    }
  }

  /**
   * Returns the MCP integration API. The instance is memoized so that configuration
   * set via `configure({ agentId, serverName })` persists across calls.
   */
  public get mcp() {
    if (!this._mcpIntegration) {
      this._mcpIntegration = mcpModule.buildMcpIntegration(this)
    }
    return this._mcpIntegration
  }

  /**
   * Returns the Delegation API for listing enrolled payment methods.
   * The instance is lazily initialized on first access — the current
   * organization pin (set via `setOrganizationId` or the constructor
   * option) and the backend API version pin (`options.version`) are
   * forwarded so the first call carries the right `X-Current-Org-Id`
   * and `Nevermined-Version` headers.
   */
  public get delegation(): DelegationAPI {
    if (!this._delegation) {
      this._delegation = DelegationAPI.getInstance({
        nvmApiKey: this.nvmApiKey,
        environment: this.environmentName,
        organizationId: this.currentOrganizationId ?? undefined,
        version: this.version,
      })
    }
    return this._delegation
  }

  /**
   * Static A2A helpers and utilities.
   * Example: Payments.a2a.buildPaymentAgentCard(...)
   */
  static a2a = { buildPaymentAgentCard }

  /**
   * Get an instance of the Payments class for server-side usage.
   *
   * @param options - The options to initialize the payments class.
   * @example
   * ```
   * const payments = Payments.getInstance({
   *   nvmApiKey: 'your-nvm-api-key',
   *   environment: 'sandbox'
   * })
   * ```
   * @returns An instance of {@link Payments}
   * @throws PaymentsError if nvmApiKey is missing.
   */
  static getInstance(options: PaymentOptions) {
    if (!options.nvmApiKey) {
      throw new PaymentsError('Nevermined API Key is required')
    }
    return new Payments(options, false)
  }

  /**
   * Get an instance of the Payments class for browser usage.
   *
   * @remarks
   * This is a browser-only function.
   *
   * @param options - The options to initialize the payments class.
   * @example
   * ```
   * const payments = Payments.getBrowserInstance({
   *   returnUrl: 'https://mysite.example',
   *   environment: 'sandbox',
   *   appId: 'my-app-id',
   *   version: '1.1'
   * })
   * ```
   * @returns An instance of {@link Payments}
   * @throws PaymentsError if returnUrl is missing.
   */
  static getBrowserInstance(options: PaymentOptions) {
    if (!options.returnUrl) {
      throw new PaymentsError('returnUrl is required')
    }
    const url = new URL(window.location.href)
    const urlNvmApiKey = url.searchParams.get('nvmApiKey') as string
    if (urlNvmApiKey) {
      url.searchParams.delete('nvmApiKey')
    }

    const urlAccountAddress = url.searchParams.get('accountAddress') as string
    if (urlAccountAddress) {
      url.searchParams.delete('accountAddress')
    }

    history.replaceState(history.state, '', url.toString())

    return new Payments(options, true)
  }

  /**
   * Initializes the Payments class.
   *
   * @param options - The options to initialize the payments class.
   * @param isBrowserInstance - Whether this instance is for browser usage.
   */
  private constructor(options: PaymentOptions, isBrowserInstance = true) {
    super(options)

    this.isBrowserInstance = isBrowserInstance
    this.initializeApi(options)
  }

  /**
   * Initializes the AI Query Protocol API.
   */
  private initializeApi(options: PaymentOptions) {
    this.plans = PlansAPI.getInstance(options)
    this.agents = AgentsAPI.getInstance(options)
    this.requests = AgentRequestsAPI.getInstance(options)
    this.observability = ObservabilityAPI.getInstance(options)
    this.organizations = OrganizationsAPI.getInstance(options)
    this.query = AIQueryApi.getInstance()
    this.contracts = new ContractsAPI(options)
    this.facilitator = FacilitatorAPI.getInstance(options)
    this.x402 = X402TokenAPI.getInstance(options)
  }

  /**
   * Initiates the connect flow. The user's browser will be redirected to
   * the Nevermined App login page.
   *
   * @remarks
   * This is a browser-only function.
   * @example
   * ```
   * payments.connect()
   * ```
   */
  public connect() {
    if (!this.isBrowserInstance) return
    const url = new URL(`/login?returnUrl=${this.returnUrl}`, this.environment.frontend)
    window.location.href = url.toString()
  }

  /**
   * Logs out the user by removing the NVM API key.
   *
   * @remarks
   * This is a browser-only function.
   * @example
   * ```
   * payments.logout()
   * ```
   */
  public logout() {
    this.nvmApiKey = ''
  }

  /**
   * Pins (or clears) the active organization workspace used by every
   * subsequent authenticated request. The SDK forwards the choice as the
   * `X-Current-Org-Id` header so the backend scopes publications and
   * other org-aware queries to the requested organization.
   *
   * Pass `null` to clear the pin and let the backend fall back to the
   * API key's org tag or the caller's most-recent active membership.
   *
   * For one-off targeting (e.g. publish a single agent into Org B without
   * leaving Org B as the active workspace) prefer the per-call
   * `{ organizationId }` option on `agents.registerAgent` /
   * `plans.registerPlan` / similar.
   *
   * @param organizationId - Org ID to pin (e.g. `org-…`) or `null` to clear.
   * @example
   * ```ts
   * payments.setOrganizationId('org-abc123')
   * await payments.agents.registerAgent(metadata, api, [planId]) // lands in org-abc123
   * ```
   */
  public override setOrganizationId(organizationId: string | null): void {
    super.setOrganizationId(organizationId)
    this.plans?.setOrganizationId(organizationId)
    this.agents?.setOrganizationId(organizationId)
    this.requests?.setOrganizationId(organizationId)
    this.observability?.setOrganizationId(organizationId)
    this.organizations?.setOrganizationId(organizationId)
    this.contracts?.setOrganizationId(organizationId)
    this.facilitator?.setOrganizationId(organizationId)
    this.x402?.setOrganizationId(organizationId)
    // `_delegation` is lazy — the getter forwards `currentOrganizationId`
    // on first access, so only propagate to an already-built instance.
    // (Eagerly constructing it here would change the lazy contract.)
    this._delegation?.setOrganizationId(organizationId)
  }

  /**
   * Checks if a user is logged in.
   * @example
   * ```
   * payments.isLoggedIn
   * ```
   * @returns True if the user is logged in.
   */
  get isLoggedIn(): boolean {
    return this.nvmApiKey.length > 0
  }
}
